import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { apiClient } from "../lib/axios-interceptor";
import {
  whenLandingReady,
  SELECTED_PAGE_STORAGE_KEY,
} from "../lib/landing";

export { SELECTED_PAGE_STORAGE_KEY };

/** How long to coalesce rapid selection changes before telling the server. */
const PERSIST_DEBOUNCE_MS = 600;

interface FacebookPageData {
  id: string;
  name: string;
  picture?: string;
  access_token?: string;
  platform?: "facebook" | "instagram";
  username?: string;
}

interface FacebookPageContextType {
  selectedPage: FacebookPageData | null;
  setSelectedPage: (page: FacebookPageData | null) => void;
  clearSelectedPage: () => void;
  isPageSelected: boolean;
  isLoading: boolean;
  /** The page the server remembers from the user's last session, if still valid. */
  rememberedPageId: string | null;
  /** True until the server's remembered page has been resolved (or timed out). */
  isRestoring: boolean;
}

const FacebookPageContext = createContext<FacebookPageContextType | undefined>(
  undefined
);

export const useFacebookPage = () => {
  const context = useContext(FacebookPageContext);
  if (context === undefined) {
    throw new Error(
      "useFacebookPage must be used within a FacebookPageProvider"
    );
  }
  return context;
};

interface FacebookPageProviderProps {
  children: ReactNode;
}

export const FacebookPageProvider: React.FC<FacebookPageProviderProps> = ({
  children,
}) => {
  const [selectedPage, setSelectedPageState] =
    useState<FacebookPageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rememberedPageId, setRememberedPageId] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  // The id we have already told the server about. Seeded on hydration so simply
  // loading the app never writes the same value back, which would otherwise
  // fire a PUT on every page load and on every PageRouteGuard restore.
  const lastPersistedIdRef = useRef<string | null>(null);
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load selected page from localStorage on mount
  useEffect(() => {
    const savedPage = localStorage.getItem(SELECTED_PAGE_STORAGE_KEY);
    if (savedPage) {
      try {
        const pageData = JSON.parse(savedPage);
        setSelectedPageState(pageData);
        lastPersistedIdRef.current = pageData?.id ?? null;
      } catch (error) {
        console.error("Error parsing saved Facebook page:", error);
        localStorage.removeItem(SELECTED_PAGE_STORAGE_KEY);
      }
    }

    // Mark loading as complete after attempting to load from localStorage.
    // Note this tracks only the synchronous cache read — consumers that block
    // rendering on it must not wait for the network.
    setIsLoading(false);
  }, []);

  // Pick up the server's remembered page once ProtectedRoute's profile-status
  // call lands, so the selector can preselect it on a device with no cache.
  useEffect(() => {
    let cancelled = false;
    whenLandingReady().then((info) => {
      if (cancelled) return;
      setRememberedPageId(info?.pageId ?? null);
      setIsRestoring(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Clean up a pending write if the provider unmounts mid-debounce.
  useEffect(() => {
    return () => {
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    };
  }, []);

  /** Remember this choice server-side so the next login (any device) lands on it. */
  const persistSelection = (pageId: string) => {
    // Never write while an admin is impersonating — that would overwrite the
    // real user's memory with wherever the admin happened to click.
    if (sessionStorage.getItem("impersonateUserId")) return;
    if (pageId === lastPersistedIdRef.current) return;

    lastPersistedIdRef.current = pageId;

    if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    writeTimerRef.current = setTimeout(() => {
      apiClient
        .put("/api/user/last-page", { pageId })
        .catch((err) =>
          console.warn(
            "⚠️ [PAGE_CTX] Failed to remember selected page:",
            err?.message
          )
        );
    }, PERSIST_DEBOUNCE_MS);
  };

  const setSelectedPage = (page: FacebookPageData | null) => {
    setSelectedPageState(page);
    if (page) {
      localStorage.setItem(SELECTED_PAGE_STORAGE_KEY, JSON.stringify(page));
      persistSelection(page.id);
    } else {
      localStorage.removeItem(SELECTED_PAGE_STORAGE_KEY);
    }
  };

  // Clears the local cache only. This runs on logout and when a cached page
  // turns out to be stale — neither is a reason to erase the user's server-side
  // memory, and the server validates that value on read anyway.
  const clearSelectedPage = () => {
    setSelectedPageState(null);
    lastPersistedIdRef.current = null;
    localStorage.removeItem(SELECTED_PAGE_STORAGE_KEY);
  };

  const isPageSelected = selectedPage !== null;

  return (
    <FacebookPageContext.Provider
      value={{
        selectedPage,
        setSelectedPage,
        clearSelectedPage,
        isPageSelected,
        isLoading,
        rememberedPageId,
        isRestoring,
      }}
    >
      {children}
    </FacebookPageContext.Provider>
  );
};
